/**
 * Google Tag Manager loader.
 *
 * Env-guarded by `VITE_GTM_ID`: when unset, the loader is a no-op and
 * `pushEvent` does nothing, so dev environments never fire ad conversions
 * accidentally. When set, the official GTM snippet is injected.
 */

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
  }
}

const GTM_ID = import.meta.env.VITE_GTM_ID as string | undefined;

let initialized = false;

export function initGTM(): void {
  if (initialized || !GTM_ID || typeof window === 'undefined' || typeof document === 'undefined') return;
  try {
    window.dataLayer = window.dataLayer ?? [];
    window.dataLayer.push({
      'gtm.start': Date.now(),
      event: 'gtm.js',
    });
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(GTM_ID)}`;
    document.head.appendChild(script);
    initialized = true;
  } catch {
    /* never throw from analytics */
  }
}

export function isGTMEnabled(): boolean {
  return initialized;
}

export function pushEvent(event: string, properties?: Record<string, unknown>): void {
  if (!initialized || typeof window === 'undefined') return;
  try {
    window.dataLayer = window.dataLayer ?? [];
    window.dataLayer.push({ event, ...properties });
  } catch {
    /* never throw from analytics */
  }
}
